import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Database, Download, Upload, Trash2, Plus, RefreshCw, Shield, FileJson,
  CheckCircle2, AlertCircle, Users, Calendar, Archive
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axcfg = { withCredentials: true };

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

const formatBytes = (bytes) => {
  if (!bytes) return "—";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
};

export const AdminBackup = () => {
  const [users, setUsers] = useState([]);
  const [backups, setBackups] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createUserId, setCreateUserId] = useState("");
  const [createLabel, setCreateLabel] = useState("");

  // Restore dialog
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreUserId, setRestoreUserId] = useState("");
  const [restoreMode, setRestoreMode] = useState("replace");
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreSelected, setRestoreSelected] = useState({});
  const [restoreFileInfo, setRestoreFileInfo] = useState(null);

  // Delete confirmation
  const [deleteId, setDeleteId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [u, b, c] = await Promise.all([
        axios.get(`${API}/admin/users`, axcfg),
        axios.get(`${API}/backup/list`, axcfg),
        axios.get(`${API}/backup/collections`, axcfg),
      ]);
      setUsers(u.data || []);
      setBackups(b.data || []);
      setCollections(c.data?.collections || []);
    } catch (err) {
      toast.error("Fehler beim Laden: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleCreate = async () => {
    if (!createUserId) { toast.error("Bitte Benutzer auswählen"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/backup/create`,
        { target_user_id: createUserId, label: createLabel || null },
        axcfg);
      toast.success("Backup erstellt");
      setCreateOpen(false);
      setCreateUserId("");
      setCreateLabel("");
      fetchAll();
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = (backup) => {
    // Open in new tab – cookies are sent automatically
    window.open(`${API}/backup/${backup.id}/download`, "_blank");
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/backup/${deleteId}`, axcfg);
      toast.success("Backup gelöscht");
      setDeleteId(null);
      fetchAll();
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleFileSelect = async (e) => {
    const f = e.target.files?.[0];
    setRestoreFile(f || null);
    setRestoreFileInfo(null);
    setRestoreSelected({});
    if (!f) return;
    try {
      const text = await f.text();
      const json = JSON.parse(text);
      const colls = json.collections ? Object.keys(json.collections) : [];
      const counts = Object.fromEntries(
        colls.map((c) => [c, (json.collections[c] || []).length])
      );
      setRestoreFileInfo({
        version: json.version,
        target_user_id: json.target_user_id,
        total: json.total_documents || colls.reduce((s, c) => s + counts[c], 0),
        collections: colls,
        counts,
      });
      const sel = {};
      colls.forEach((c) => { sel[c] = counts[c] > 0; });
      setRestoreSelected(sel);
    } catch (err) {
      toast.error("Backup-Datei ungültig: " + err.message);
      setRestoreFile(null);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) { toast.error("Bitte Backup-Datei wählen"); return; }
    if (!restoreUserId) { toast.error("Bitte Ziel-Benutzer wählen"); return; }

    const selected = Object.keys(restoreSelected).filter((k) => restoreSelected[k]);
    if (selected.length === 0) { toast.error("Bitte mindestens einen Bereich wählen"); return; }

    const confirmMsg = restoreMode === "replace"
      ? `Wirklich wiederherstellen? Dies löscht ${selected.length} Datenbereiche des Ziel-Benutzers vor dem Import!`
      : `Wirklich wiederherstellen? ${selected.length} Datenbereiche werden ergänzt (nur fehlende Datensätze).`;
    if (!window.confirm(confirmMsg)) return;

    setRestoring(true);
    try {
      const form = new FormData();
      form.append("file", restoreFile);
      form.append("target_user_id", restoreUserId);
      form.append("collections", selected.join(","));
      form.append("mode", restoreMode);
      const res = await axios.post(`${API}/backup/restore`, form, {
        ...axcfg,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const report = res.data?.report || {};
      const totalInserted = Object.values(report).reduce((s, r) => s + (r.inserted || 0), 0);
      toast.success(`Wiederhergestellt: ${totalInserted} Datensätze in ${selected.length} Bereichen`);
      setRestoreOpen(false);
      setRestoreFile(null);
      setRestoreFileInfo(null);
      setRestoreSelected({});
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    } finally {
      setRestoring(false);
    }
  };

  const userName = (uid) => {
    const u = users.find((x) => x.user_id === uid);
    return u ? (u.name || u.username || u.email || uid) : uid;
  };

  return (
    <div data-testid="admin-backup-page">
      <header className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title flex items-center gap-2">
              <Database size={22} className="text-violet-400" />
              Backup & Wiederherstellung
            </h1>
            <p className="page-subtitle">
              Admin-Bereich · Datensicherung pro Benutzer
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={fetchAll}
              disabled={loading}
              className="border-slate-600 text-slate-200 hover:bg-slate-800"
              data-testid="refresh-backups-btn"
            >
              <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
              Aktualisieren
            </Button>
            <Button
              onClick={() => setRestoreOpen(true)}
              variant="outline"
              className="border-amber-600 text-amber-300 hover:bg-amber-600/10"
              data-testid="open-restore-btn"
            >
              <Upload size={14} className="mr-2" />
              Wiederherstellen
            </Button>
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-violet-600 hover:bg-violet-500 text-white"
              data-testid="open-create-btn"
            >
              <Plus size={14} className="mr-2" />
              Backup erstellen
            </Button>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-10 py-6">
        {/* Info-Banner */}
        <div className="mb-6 rounded-xl border border-violet-500/30 bg-violet-500/5 p-4 flex items-start gap-3">
          <Shield size={18} className="text-violet-400 mt-0.5 shrink-0" />
          <div className="text-sm text-slate-300">
            <p className="font-medium text-white mb-1">Admin-only · Nur du kannst Backups erstellen und wiederherstellen.</p>
            <p className="text-slate-400">
              Ein Backup enthält alle user-spezifischen Daten ({collections.length} Collections): Stammdaten,
              Umsätze, Schichtpläne, HACCP, Monatsberichte und mehr. Backups werden auf dem Server
              gespeichert und können als JSON heruntergeladen werden.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="rounded-xl border border-slate-700/60 bg-slate-900/40 p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Backups</p>
            <p className="text-2xl font-semibold text-white">{backups.length}</p>
          </div>
          <div className="rounded-xl border border-slate-700/60 bg-slate-900/40 p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Benutzer</p>
            <p className="text-2xl font-semibold text-white">{users.length}</p>
          </div>
          <div className="rounded-xl border border-slate-700/60 bg-slate-900/40 p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Collections pro Backup</p>
            <p className="text-2xl font-semibold text-white">{collections.length}</p>
          </div>
        </div>

        {/* Liste */}
        <div className="rounded-xl border border-slate-700/60 bg-slate-900/40 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-700/60 flex items-center gap-2">
            <Archive size={16} className="text-slate-400" />
            <h2 className="text-base font-semibold text-white">Alle Backups</h2>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-400">Laden...</div>
          ) : backups.length === 0 ? (
            <div className="p-12 text-center">
              <Database size={32} className="mx-auto text-slate-600 mb-3" />
              <p className="text-slate-400 text-sm">Noch keine Backups vorhanden.</p>
              <Button
                onClick={() => setCreateOpen(true)}
                className="mt-4 bg-violet-600 hover:bg-violet-500"
                data-testid="empty-create-btn"
              >
                <Plus size={14} className="mr-2" />
                Erstes Backup erstellen
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-700/60">
              {backups.map((b) => (
                <div key={b.id} className="p-5 flex flex-wrap items-start justify-between gap-4 hover:bg-slate-800/30"
                     data-testid={`backup-row-${b.id}`}>
                  <div className="flex-1 min-w-[250px]">
                    <div className="flex items-center gap-2 mb-1">
                      <FileJson size={16} className="text-violet-400 shrink-0" />
                      <h3 className="font-medium text-white">{b.label}</h3>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-400">
                      <span className="inline-flex items-center gap-1">
                        <Users size={11} /> {userName(b.target_user_id)}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={11} /> {formatDate(b.created_at)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-emerald-400">
                        <CheckCircle2 size={11} /> {b.total_documents} Datensätze
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(b)}
                      className="border-slate-600 text-slate-200 hover:bg-slate-800"
                      data-testid={`download-${b.id}`}
                    >
                      <Download size={13} className="mr-1.5" />
                      Download
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteId(b.id)}
                      className="border-red-700/60 text-red-300 hover:bg-red-900/20"
                      data-testid={`delete-${b.id}`}
                    >
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="create-backup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus size={18} className="text-violet-400" />
              Backup erstellen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Benutzer wählen *</Label>
              <Select value={createUserId} onValueChange={setCreateUserId}>
                <SelectTrigger data-testid="create-user-select">
                  <SelectValue placeholder="Benutzer auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name || u.username || u.email || u.user_id}
                      {u.role === "admin" && " · Admin"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Label (optional)</Label>
              <Input
                value={createLabel}
                onChange={(e) => setCreateLabel(e.target.value)}
                placeholder="z.B. Vor Jahresabschluss"
                data-testid="create-label-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
              Abbrechen
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !createUserId}
              className="bg-violet-600 hover:bg-violet-500"
              data-testid="confirm-create-btn"
            >
              {creating ? "Erstelle..." : "Backup erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog open={restoreOpen} onOpenChange={(o) => {
        setRestoreOpen(o);
        if (!o) { setRestoreFile(null); setRestoreFileInfo(null); setRestoreSelected({}); }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="restore-backup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload size={18} className="text-amber-400" />
              Backup wiederherstellen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2">
              <AlertCircle size={16} className="text-amber-400 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-200">
                Im Modus <strong>"Ersetzen"</strong> werden bestehende Daten des Ziel-Benutzers <strong>gelöscht</strong>,
                bevor das Backup importiert wird. Im Modus <strong>"Ergänzen"</strong> werden nur fehlende Datensätze
                hinzugefügt (keine Überschreibung).
              </p>
            </div>

            <div className="space-y-2">
              <Label>Backup-Datei (.json) *</Label>
              <Input
                type="file"
                accept="application/json,.json"
                onChange={handleFileSelect}
                className="cursor-pointer"
                data-testid="restore-file-input"
              />
              {restoreFileInfo && (
                <div className="text-xs text-slate-400 mt-2 rounded bg-slate-800/60 p-2.5 space-y-1">
                  <p>Version: <span className="text-slate-200">{restoreFileInfo.version}</span></p>
                  <p>Ursprungs-User: <span className="text-slate-200 font-mono">{restoreFileInfo.target_user_id}</span></p>
                  <p>Datensätze gesamt: <span className="text-emerald-400">{restoreFileInfo.total}</span></p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Ziel-Benutzer *</Label>
              <Select value={restoreUserId} onValueChange={setRestoreUserId}>
                <SelectTrigger data-testid="restore-user-select">
                  <SelectValue placeholder="Wo soll wiederhergestellt werden?" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name || u.username || u.email || u.user_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Modus</Label>
              <Select value={restoreMode} onValueChange={setRestoreMode}>
                <SelectTrigger data-testid="restore-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="replace">Ersetzen (bestehende Daten löschen)</SelectItem>
                  <SelectItem value="merge">Ergänzen (nur fehlende Datensätze)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {restoreFileInfo && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Bereiche wiederherstellen</Label>
                  <div className="flex gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => {
                        const all = {};
                        restoreFileInfo.collections.forEach((c) => { all[c] = true; });
                        setRestoreSelected(all);
                      }}
                      className="text-violet-400 hover:text-violet-300"
                      data-testid="select-all-btn"
                    >
                      Alle
                    </button>
                    <span className="text-slate-600">·</span>
                    <button
                      type="button"
                      onClick={() => setRestoreSelected({})}
                      className="text-violet-400 hover:text-violet-300"
                      data-testid="select-none-btn"
                    >
                      Keine
                    </button>
                    <span className="text-slate-600">·</span>
                    <button
                      type="button"
                      onClick={() => {
                        const sel = {};
                        restoreFileInfo.collections.forEach((c) => {
                          sel[c] = restoreFileInfo.counts[c] > 0;
                        });
                        setRestoreSelected(sel);
                      }}
                      className="text-violet-400 hover:text-violet-300"
                      data-testid="select-nonempty-btn"
                    >
                      Nur mit Daten
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 rounded-lg bg-slate-800/40 border border-slate-700/60 max-h-64 overflow-y-auto">
                  {restoreFileInfo.collections.map((c) => {
                    const count = restoreFileInfo.counts[c] || 0;
                    return (
                      <label
                        key={c}
                        className={`flex items-center gap-2 text-xs cursor-pointer p-1.5 rounded hover:bg-slate-700/50 ${
                          count === 0 ? "opacity-50" : ""
                        }`}
                      >
                        <Checkbox
                          checked={!!restoreSelected[c]}
                          onCheckedChange={(checked) =>
                            setRestoreSelected({ ...restoreSelected, [c]: !!checked })
                          }
                          data-testid={`restore-check-${c}`}
                        />
                        <span className="flex-1 truncate text-slate-200">{c}</span>
                        <span className="text-slate-500 tabular-nums">{count}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreOpen(false)} disabled={restoring}>
              Abbrechen
            </Button>
            <Button
              onClick={handleRestore}
              disabled={restoring || !restoreFile || !restoreUserId}
              className="bg-amber-600 hover:bg-amber-500"
              data-testid="confirm-restore-btn"
            >
              {restoring ? "Stelle wieder her..." : "Wiederherstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 size={18} className="text-red-400" />
              Backup löschen?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-400">
            Dieses Backup wird dauerhaft entfernt. Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Abbrechen</Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-500"
              data-testid="confirm-delete-btn"
            >
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBackup;
